import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  RotateCcw, 
  Maximize2, 
  ChevronLeft, 
  ChevronRight,
  Image as ImageIcon,
  Download,
  Home,
  Expand,
  X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { FileSystemNavigator, FileSystemItem } from '../../utils/filesystem';

interface ImageViewerProps {
  imagePath?: string;
  imageUrl?: string;
  navigator?: FileSystemNavigator;
  onOpenFile?: (filePath: string, content: string) => void;
  onClose?: () => void;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({ 
  imagePath = '', 
  imageUrl = '',
  navigator,
  onOpenFile,
  onClose 
}) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [allImages, setAllImages] = useState<FileSystemItem[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentImagePath, setCurrentImagePath] = useState<string>('');
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Use internal state if available, otherwise use props
  const displayImagePath = currentImagePath || imagePath;
  const displayImageUrl = currentImageUrl || imageUrl;
  const fileName = displayImagePath ? displayImagePath.split('\\').pop() || 'Unknown Image' : 'Image';
  const folderPath = imagePath ? imagePath.substring(0, imagePath.lastIndexOf('\\')) || 'C:\\' : 'C:\\';

  // Get all images in the current folder
  useEffect(() => {
    if (navigator && imagePath) {
      console.log('Loading images from folder:', folderPath);
      const items = navigator.getItemsInPath(folderPath);
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif'];
      const images = items.filter(item => 
        item.type === 'file' && 
        item.extension && 
        imageExtensions.includes(item.extension.toLowerCase())
      );
      console.log('Found images:', images.length);
      setAllImages(images);
      
      // Find current image index
      const currentIndex = images.findIndex(img => img.path === imagePath);
      console.log('Current image index:', currentIndex);
      setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
    } else {
      setAllImages([]);
      setCurrentImageIndex(0);
    }
  }, [navigator, imagePath, folderPath]);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setZoom(100);
    setRotation(0);
  }, [imageUrl, imagePath]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 500));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };

  const handleRotateLeft = () => {
    setRotation(prev => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation(prev => prev + 90);
  };

  const handleFitToWindow = () => {
    if (containerRef.current && imageRef.current && imageLoaded) {
      const container = containerRef.current;
      const image = imageRef.current;
      
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imageWidth = image.naturalWidth;
      const imageHeight = image.naturalHeight;
      
      // Calculate scale to fit both width and height
      const scaleX = containerWidth / imageWidth;
      const scaleY = containerHeight / imageHeight;
      const scale = Math.min(scaleX, scaleY);
      
      // Convert to percentage and apply
      const newZoom = Math.round(scale * 100);
      setZoom(Math.max(newZoom, 10)); // Minimum 10% zoom
      setRotation(0);
    } else {
      // Fallback if refs aren't available
      setZoom(100);
      setRotation(0);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoaded(false);
    setImageError(true);
  };

  const handlePreviousImage = () => {
    if (allImages.length > 0 && currentImageIndex > 0) {
      const prevIndex = currentImageIndex - 1;
      const prevImage = allImages[prevIndex];
      console.log('Going to previous image:', prevImage.path);
      setCurrentImageIndex(prevIndex);
      setCurrentImagePath(prevImage.path);
      setCurrentImageUrl(prevImage.content || '');
      // Reset zoom and rotation for new image
      setZoom(100);
      setRotation(0);
    }
  };

  const handleNextImage = () => {
    if (allImages.length > 0 && currentImageIndex < allImages.length - 1) {
      const nextIndex = currentImageIndex + 1;
      const nextImage = allImages[nextIndex];
      console.log('Going to next image:', nextImage.path);
      setCurrentImageIndex(nextIndex);
      setCurrentImagePath(nextImage.path);
      setCurrentImageUrl(nextImage.content || '');
      // Reset zoom and rotation for new image
      setZoom(100);
      setRotation(0);
    }
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const canGoPrevious = allImages.length > 0 && currentImageIndex > 0;
  const canGoNext = allImages.length > 0 && currentImageIndex < allImages.length - 1;

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isFullscreen]);

  // Render fullscreen view
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex flex-col">
        {/* Fullscreen Controls */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            disabled={!canGoPrevious}
            onClick={handlePreviousImage}
            className="bg-black/50 hover:bg-black/70 text-white gap-1"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            disabled={!canGoNext}
            onClick={handleNextImage}
            className="bg-black/50 hover:bg-black/70 text-white gap-1"
          >
            <ChevronRight size={16} />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleToggleFullscreen}
            className="bg-black/50 hover:bg-black/70 text-white"
          >
            <X size={16} />
          </Button>
        </div>

        {/* Fullscreen Image */}
        <div className="flex-1 flex items-center justify-center p-4">
          {displayImageUrl ? (
            <img
              src={displayImageUrl}
              alt={fileName}
              className="max-w-full max-h-full object-contain"
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transformOrigin: 'center'
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-white">
              <ImageIcon size={64} className="mb-4 opacity-50" />
              <p className="text-lg">No Image Selected</p>
            </div>
          )}
        </div>

        {/* Fullscreen Info Bar */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded">
          <span className="text-sm">{fileName}</span>
          {allImages.length > 0 && (
            <span className="text-xs ml-4 opacity-70">
              {currentImageIndex + 1} of {allImages.length}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Menu Bar */}
      <div className="border-b border-border bg-muted/50">
        <div className="flex items-center gap-1 px-2 py-1">
          <Button variant="ghost" size="sm" className="text-xs px-2 py-1 h-auto">
            File
          </Button>
          <Button variant="ghost" size="sm" className="text-xs px-2 py-1 h-auto">
            Edit
          </Button>
          <Button variant="ghost" size="sm" className="text-xs px-2 py-1 h-auto">
            View
          </Button>
          <Button variant="ghost" size="sm" className="text-xs px-2 py-1 h-auto">
            Tools
          </Button>
          <Button variant="ghost" size="sm" className="text-xs px-2 py-1 h-auto">
            Help
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b border-border p-2 bg-muted/30">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleZoomOut} className="gap-1">
            <ZoomOut size={14} />
            Zoom Out
          </Button>
          <Button variant="ghost" size="sm" onClick={handleZoomIn} className="gap-1">
            <ZoomIn size={14} />
            Zoom In
          </Button>
          <span className="text-sm text-muted-foreground px-2">
            {zoom}%
          </span>
          <div className="w-px h-6 bg-border mx-1" />
          <Button variant="ghost" size="sm" onClick={handleRotateLeft} className="gap-1">
            <RotateCcw size={14} />
            Rotate Left
          </Button>
          <Button variant="ghost" size="sm" onClick={handleRotateRight} className="gap-1">
            <RotateCw size={14} />
            Rotate Right
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button variant="ghost" size="sm" onClick={handleFitToWindow} className="gap-1">
            <Maximize2 size={14} />
            Fit to Window
          </Button>
          <Button variant="ghost" size="sm" onClick={handleToggleFullscreen} className="gap-1">
            <Expand size={14} />
            Fullscreen
          </Button>
          <Button variant="ghost" size="sm" className="gap-1">
            <Download size={14} />
            Save As
          </Button>
        </div>
      </div>

      {/* Image Info */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/20">
        <ImageIcon size={16} className="text-blue-600" />
        <span className="text-sm font-medium">{fileName}</span>
        {displayImagePath && (
          <span className="text-xs text-muted-foreground">
            - {displayImagePath}
          </span>
        )}
      </div>

      {/* Image Display Area */}
      <div ref={containerRef} className="flex-1 flex items-center justify-center bg-gray-100 dark:bg-gray-900 overflow-hidden">
        {displayImageUrl ? (
          <div className="relative flex items-center justify-center w-full h-full">
            {!imageLoaded && !imageError && (
              <div className="flex flex-col items-center justify-center text-muted-foreground">
                <ImageIcon size={48} className="mb-2 opacity-50" />
                <p className="text-sm">Loading image...</p>
              </div>
            )}
            
            {imageError && (
              <div className="flex flex-col items-center justify-center text-muted-foreground">
                <ImageIcon size={48} className="mb-2 opacity-50" />
                <p className="text-sm">Failed to load image</p>
                <p className="text-xs mt-1">The image file may be corrupted or missing</p>
              </div>
            )}
            
            <img
              ref={imageRef}
              src={displayImageUrl}
              alt={fileName}
              className={cn(
                "max-w-full max-h-full object-contain transition-all duration-200",
                !imageLoaded && "opacity-0",
                imageError && "hidden"
              )}
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transformOrigin: 'center'
              }}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-muted-foreground">
            <ImageIcon size={64} className="mb-4 opacity-50" />
            <p className="text-lg">No Image Selected</p>
            <p className="text-sm mt-2">Open an image file to view it here</p>
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      <div className="border-t border-border p-2 bg-muted/30 flex items-center justify-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          disabled={!canGoPrevious}
          onClick={handlePreviousImage}
          className="gap-1"
        >
          <ChevronLeft size={14} />
          Previous
        </Button>
        <span className="text-xs text-muted-foreground px-4">
          {allImages.length > 0 ? `${currentImageIndex + 1} of ${allImages.length}` : '1 of 1'}
        </span>
        <Button 
          variant="ghost" 
          size="sm" 
          disabled={!canGoNext}
          onClick={handleNextImage}
          className="gap-1"
        >
          Next
          <ChevronRight size={14} />
        </Button>
      </div>

      {/* Status Bar */}
      <div className="border-t border-border px-4 py-1 bg-muted/50 text-xs text-muted-foreground flex justify-between shrink-0">
        <span>
          {imageLoaded ? 'Image loaded successfully' : imageError ? 'Error loading image' : 'Ready'}
        </span>
        <span>Zoom: {zoom}% | Rotation: {rotation}°</span>
      </div>
    </div>
  );
};